import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Container,
  TextField,
  Autocomplete,
  Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';

// Custom styles for dropdown
const dropdownMenuProps = {
  PaperProps: {
    style: {
      maxHeight: '300px'
    },
  },
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
};

// Style for MenuItem to make them compact
const compactMenuItemStyle = {
  fontSize: '0.875rem',
  paddingTop: '6px',
  paddingBottom: '6px',
  minHeight: '32px',
};

// Mobile-friendly Select with search
const SearchableSelect = ({
  id,
  value,
  onChange,
  onBlur,
  label,
  options,
  error,
  touched,
  helperText,
  disabled = false,
  placeholder = 'Search...',
  getOptionLabel = (option) => option.label || option,
  getOptionValue = (option) => option.value || option,
  noOptionsText = 'No options available',
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter options based on search term
  const filteredOptions = options.filter(option => {
    const label = getOptionLabel(option).toLowerCase();
    return label.includes(searchTerm.toLowerCase());
  });

  // Get selected option
  const selectedOption = options.find(opt => getOptionValue(opt) === value) || null;

  // Handle selection
  const handleSelectChange = (event, newValue) => {
    const newId = newValue ? getOptionValue(newValue) : '';
    onChange({
      target: {
        value: newId,
      },
    });
    setSearchTerm('');
    setOpen(false);
  };

  // Handle input change
  const handleInputChange = (event, newInputValue) => {
    setSearchTerm(newInputValue);
  };

  // Handle blur
  const handleBlur = () => {
    if (onBlur) {
      onBlur({
        target: {
          value: value,
        },
      });
    }
    // Close dropdown on mobile after selection
    if (isMobile) {
      setTimeout(() => setOpen(false), 300);
    }
  };

  return (
    <Autocomplete
      id={id}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setSearchTerm('');
      }}
      value={selectedOption}
      onChange={handleSelectChange}
      onInputChange={handleInputChange}
      options={filteredOptions}
      getOptionLabel={getOptionLabel}
      disabled={disabled}
      loading={options.length === 0}
      loadingText="Loading..."
      noOptionsText={noOptionsText}
      clearOnEscape
      blurOnSelect
      handleHomeEndKeys
      isOptionEqualToValue={(option, value) => {
        return getOptionValue(option) === getOptionValue(value);
      }}
      renderOption={(props, option) => (
        <li {...props} style={{ fontSize: '0.875rem', padding: '8px 16px' }}>
          {getOptionLabel(option)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required
          error={!!error && touched}
          helperText={touched && helperText ? helperText : ''}
          placeholder={isMobile ? placeholder : ''}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {params.InputProps.endAdornment}
                {value && (
                  <Chip
                    label="Clear"
                    size="small"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => {
                      onChange({
                        target: { value: '' }
                      });
                      setSearchTerm('');
                    }}
                    sx={{ ml: 1 }}
                  />
                )}
              </>
            ),
          }}
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: true,
          }}
        />
      )}
      // Mobile-friendly props
      disablePortal={!isMobile}
      popupIcon={isMobile ? <SearchIcon /> : undefined}
      sx={{
        '& .MuiAutocomplete-popupIndicator': {
          transform: 'rotate(0deg)',
        },
        '& .MuiAutocomplete-clearIndicator': {
          display: 'none', // Hide default clear, we have custom
        },
        // Mobile touch improvements
        '& .MuiAutocomplete-inputRoot': {
          paddingRight: '32px !important',
          '& .MuiAutocomplete-input': {
            padding: isMobile ? '10px 4px' : '8px 4px',
            fontSize: isMobile ? '16px' : '0.875rem', // Prevent zoom on iOS
          },
        },
        '& .MuiAutocomplete-popper': {
          '& .MuiAutocomplete-listbox': {
            maxHeight: isMobile ? '300px' : '200px',
            '& li': {
              padding: isMobile ? '12px 16px' : '8px 16px',
              fontSize: isMobile ? '16px' : '0.875rem',
              minHeight: isMobile ? '44px' : '32px', // Better touch targets
            },
          },
        },
      }}
    />
  );
};

const CompanyPartnerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [foreignAgents, setForeignAgents] = useState([]);
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  
  const [formData, setFormData] = useState({
    companyID: '',
    foreignAgent: '',
    isActive: true,
  });

  const isEdit = !!id;

  useEffect(() => {
    fetchCompanies();
    fetchForeignAgents();
  }, []);

  useEffect(() => {
    if (isEdit) {
      fetchPartner();
    } else if (location.state?.companyId) {
      setFormData(prev => ({
        ...prev,
        companyID: location.state.companyId
      }));
    }
  }, [id, location.state]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/Companies/active-agencies');
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError('Failed to load companies. Please try again.');
    }
  };

  const fetchForeignAgents = async () => {
    try {
      const response = await api.get('/Companies/all_agents');
      setForeignAgents(response.data || []);
    } catch (error) {
      console.error('Error fetching foreign agents:', error);
      setError('Failed to load foreign agents. Please try again.');
    }
  };

  const fetchPartner = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(`/CompanyPartners/${id}`);
      const data = response.data;
      setFormData({
        companyID: data.companyID || '',
        foreignAgent: data.foreignAgentID || '',
        isActive: data.isActive !== undefined ? data.isActive : true,
      });
    } catch (error) {
      console.error('Error fetching partner:', error);
      setError('Failed to load partner data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const requiredFields = {
    companyID: 'Company is required',
    foreignAgent: 'Foreign Agent is required',
  };

  const validateField = (field, value) => {
    if (requiredFields[field] && !value) {
      return requiredFields[field];
    }
    return null;
  };

  const validateForm = () => {
    const errors = {};
    const touched = {};
    let hasErrors = false;

    Object.keys(requiredFields).forEach(field => {
      touched[field] = true;
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
        hasErrors = true;
      }
    });

    setValidationErrors(errors);
    setTouchedFields(touched);
    return !hasErrors;
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleBlur = (field) => (event) => {
    setTouchedFields(prev => ({
      ...prev,
      [field]: true
    }));
    
    const error = validateField(field, formData[field]);
    if (error) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleSubmit = async () => {
    const isValid = validateForm();
    
    if (!isValid) {
      const firstErrorField = Object.keys(validationErrors)[0];
      if (firstErrorField) {
        const element = document.getElementById(`field-${firstErrorField}`);
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }, 300);
        }
      }
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const submitData = {
        companyID: formData.companyID,
        foreignAgent: formData.foreignAgent,
        isActive: formData.isActive,
      };

      if (isEdit) {
        submitData.partnerID = id;
        await api.put(`/CompanyPartners/${id}`, submitData);
      } else {
        await api.post('/CompanyPartners', submitData);
      }

      navigate('/companies/companypartners');
    } catch (error) {
      console.error('Error saving partner:', error);
      if (error.response) {
        if (error.response.status === 409) {
          setError('This partner relationship already exists.');
        } else {
          setError(`Failed to save partner: ${error.response.data || error.response.statusText}`);
        }
      } else {
        setError('Failed to save partner. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/companies/companypartners');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ p: { xs: 1, sm: 3 } }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1" fontSize={{ xs: '1.2rem', sm: '1.5rem' }}>
            {isEdit ? 'Edit Partner' : 'Create New Partner'}
          </Typography>
          {/* Desktop buttons - hidden on mobile */}
          <Box display={{ xs: 'none', sm: 'block' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={isSaving}
              sx={{ mr: 1 }}
              size="small"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              size="small"
            >
              Cancel
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <Card sx={{ bgcolor: '#fafafa' }}>
          <CardContent>
            <Grid container spacing={3} direction="column">
              {/* Company */}
              <Grid item xs={12}>
                <SearchableSelect
                  id="field-companyID"
                  value={formData.companyID}
                  onChange={handleChange('companyID')}
                  onBlur={handleBlur('companyID')}
                  label="Company"
                  options={companies}
                  getOptionLabel={(company) => `${company.companyName} - ${company.country || 'N/A'}`}
                  getOptionValue={(company) => company.companyID}
                  error={validationErrors.companyID}
                  touched={touchedFields.companyID}
                  helperText={validationErrors.companyID}
                  placeholder="Search for a company..."
                  noOptionsText="No companies found"
                />
              </Grid>

              {/* Foreign Agent */}
              <Grid item xs={12}>
                <SearchableSelect
                  id="field-foreignAgent"
                  value={formData.foreignAgent}
                  onChange={handleChange('foreignAgent')}
                  onBlur={handleBlur('foreignAgent')}
                  label="Foreign Agent"
                  options={foreignAgents}
                  getOptionLabel={(agent) => `${agent.companyName} - ${agent.country || 'N/A'}`}
                  getOptionValue={(agent) => agent.companyID}
                  error={validationErrors.foreignAgent}
                  touched={touchedFields.foreignAgent}
                  helperText={validationErrors.foreignAgent}
                  placeholder="Search for a foreign agent..."
                  noOptionsText="No foreign agents found"
                />
              </Grid>

              {/* Is Active Switch */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleChange('isActive')}
                      color="primary"
                      size="small"
                    />
                  }
                  label={formData.isActive ? 'Active' : 'Inactive'}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Info message */}
        <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
          <Typography variant="body2" color="textSecondary" fontSize="0.8rem">
            <strong>Note:</strong> A partner relationship links a company with a foreign agent. 
            Each company can have multiple foreign agents, but each combination of company and 
            foreign agent must be unique.
          </Typography>
        </Box>

        {/* Action Buttons - Full width on mobile, inline on desktop */}
        <Box 
          display="flex" 
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="center" 
          gap={2} 
          mt={3} 
          pt={2} 
          borderTop={1} 
          borderColor="divider"
        >
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={isSaving}
            fullWidth={true}
            sx={{ 
              py: { xs: 1.5, sm: 1 },
              fontSize: { xs: '1rem', sm: '0.9375rem' },
              maxWidth: { sm: '130px' },
              borderRadius: 2,
              '&:active': {
                transform: 'scale(0.98)',
              },
            }}
          >
            {isSaving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            fullWidth={true}
            sx={{ 
              py: { xs: 1.5, sm: 1 },
              fontSize: { xs: '1rem', sm: '0.9375rem' },
              maxWidth: { sm: '130px' },
              borderRadius: 2,
              '&:active': {
                transform: 'scale(0.98)',
              },
            }}
          >
            Cancel
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default CompanyPartnerForm;